import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { toast } from 'sonner';
import {
  CreditCard,
  Calendar,
  Download,
  AlertCircle,
  CheckCircle2,
  Loader2,
  XCircle,
  RefreshCw,
  ExternalLink,
  Crown,
  Sparkles
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingSection = ({ user, onSubscribe }) => {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load subscription status
      const subResponse = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(subResponse.data);

      // Load invoices
      const invoicesResponse = await axios.get(`${API}/subscription/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(invoicesResponse.data.invoices);
    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription will be canceled at the end of the billing period');
      setCancelDialogOpen(false);
      loadSubscriptionData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscription/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription reactivated successfully!');
      loadSubscriptionData();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency) => {
    return `LKR ${(amount / 100).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-500', icon: CheckCircle2, text: 'Active' },
      trialing: { color: 'bg-blue-500', icon: Sparkles, text: 'Free Trial' },
      past_due: { color: 'bg-yellow-500', icon: AlertCircle, text: 'Payment Due' },
      canceled: { color: 'bg-gray-500', icon: XCircle, text: 'Canceled' },
      no_subscription: { color: 'bg-gray-400', icon: AlertCircle, text: 'No Subscription' }
    };

    const config = statusConfig[status] || statusConfig.no_subscription;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No subscription
  if (!subscription?.subscription) {
    return (
      <Card className="p-8 text-center">
        <Crown className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-xl font-bold mb-2">No Active Subscription</h3>
        <p className="text-muted-foreground mb-6">
          Subscribe now to unlock unlimited access to daily MCQ challenges
        </p>
        <Button onClick={onSubscribe} size="lg" className="btn-hover">
          <Sparkles className="w-4 h-4 mr-2" />
          View Plans
        </Button>
      </Card>
    );
  }

  const sub = subscription.subscription;
  const isActive = sub.status === 'active' || sub.status === 'trialing';
  const isCanceling = sub.cancel_at_period_end;

  return (
    <div className="space-y-6">
      {/* Subscription Status Card */}
      <Card className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Current Subscription</h3>
            {getStatusBadge(sub.status)}
          </div>
          <CreditCard className="w-8 h-8 text-muted-foreground" />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Plan</p>
            <p className="font-semibold">{sub.plan_name || 'Premium Plan'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {isCanceling ? 'Expires On' : 'Next Billing Date'}
            </p>
            <p className="font-semibold">{formatDate(sub.current_period_end)}</p>
          </div>
        </div>

        {isCanceling && (
          <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-900 dark:text-yellow-100">
                  Subscription Canceling
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Your subscription will end on {formatDate(sub.current_period_end)}. You'll continue to have access until then.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {isCanceling ? (
            <Button
              onClick={handleReactivateSubscription}
              disabled={reactivating}
              variant="default"
            >
              {reactivating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reactivating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reactivate Subscription
                </>
              )}
            </Button>
          ) : (
            isActive && (
              <Button
                onClick={() => setCancelDialogOpen(true)}
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                Cancel Subscription
              </Button>
            )
          )}
        </div>
      </Card>

      {/* Payment History */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Payment History</h3>
        
        {invoices.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No payments yet</p>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-medium">
                      {formatCurrency(invoice.amount_paid, invoice.currency)}
                    </p>
                    <Badge variant={invoice.status === 'paid' ? 'default' : 'destructive'}>
                      {invoice.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(invoice.created)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {invoice.invoice_pdf && (
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                    >
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </a>
                    </Button>
                  )}
                  {invoice.hosted_invoice_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      asChild
                    >
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription?</DialogTitle>
            <DialogDescription>
              Your subscription will remain active until {formatDate(sub.current_period_end)}. 
              After that, you'll lose access to premium features.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={canceling}
            >
              {canceling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Canceling...
                </>
              ) : (
                'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingSection;
